import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  OnDestroy,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { ADYEN_ERROR } from '@constants/adyen-error.constants';
import { ADYEN_HOSTED } from '@constants/adyen-hosted.constants';
import { ADYEN_RESUME } from '@constants/adyen-resume.constants';
import { environment } from '@environments/environment';
import { ADYEN_RESULT_CODE } from '@constants/adyen-result-code.constants';
import { AdyenHostedResultResponse, CreateAdyenSessionResponse } from '@models/adyen.model';
import { AdyenResume, AdyenResumeKind } from '@models/adyen-resume.model';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { captureException } from '@sentry/angular';
import { AdyenResumeService } from '@services/feature/adyen-resume.service';
import { BookingService } from '@services/feature/booking.service';
import { CustomEventService } from '@services/feature/custom-event.service';
import { PaymentService } from '@services/feature/payment.service';
import { WidgetService } from '@services/feature/widget.service';
import { LightDomProjectionDirective } from '@directives/light-dom-projection.directive';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { buildAdyenBookingContext } from '@services/utils/adyen-booking-context.util';
import { adyenSessionErrorMessageKey } from '@services/utils/adyen-error.util';
import { buildAdyenHoldScope } from '@services/utils/adyen-hold-scope.util';
import { getDomRoot } from '@services/utils/dom-root.util';
import { adyenApiOrigin, adyenHostedMessageResult } from '@services/utils/adyen-hosted.util';
import { AdyenLoaderService } from '@services/utils/adyen-loader.service';
import { LoggerService } from '@services/utils/logger.service';

/**
 * Adyen Drop-in host for the smart-widget.
 *
 * Same contract as the widget's component — Sessions flow, opaque handles only,
 * the backend decides what was actually paid — but wired to the smart-widget's
 * `CustomEventService` bus rather than `@Output()`s, matching how `app-stripe`
 * and `cw-paypal` already talk to `payment-method-view` here.
 *
 * Note the smart-widget has no Saferpay at all, so this follows the
 * Stripe/PayPal shape in this repo, not the widget's Saferpay shape.
 */
@Component({
  selector: 'app-cw-adyen',
  templateUrl: './cw-adyen.component.html',
  styleUrls: ['./cw-adyen.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule, LightDomProjectionDirective, ButtonComponent],
})
export class CwAdyenComponent implements OnInit, OnDestroy {

  readonly price = input<number>();

  private readonly dropinContainer = viewChild<ElementRef<HTMLDivElement>>('dropinContainer');

  private adyenLoaderService = inject(AdyenLoaderService);
  private adyenResumeService = inject(AdyenResumeService);
  private bookingService = inject(BookingService);
  private customEventService = inject(CustomEventService);
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private injector = inject(Injector);
  private paymentService = inject(PaymentService);
  private translateService = inject(TranslateService);
  protected widgetService = inject(WidgetService);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  /**
   * Set when the backend minted a HOSTED session: the page's origin is not registered on the Adyen
   * client key, so Drop-in would refuse to mount here. The pane then shows a pay button that opens
   * Adyen's own page in a popup instead of loading the SDK.
   */
  protected readonly hostedSession = signal<CreateAdyenSessionResponse | null>(null);
  /** The browser refused the popup; the button stays so a press after allowing it works. */
  protected readonly hostedPopupBlocked = signal(false);
  /** A hosted result has been handed on - exactly once per session. */
  protected readonly hostedPaid = signal(false);
  protected readonly hostedI18n = ADYEN_HOSTED.I18N;
  protected readonly resumeI18n = ADYEN_RESUME.I18N;
  protected readonly hostedPopupName = ADYEN_HOSTED.POPUP_NAME;
  /**
   * A hosted payment is being waited for. The pane then offers a "Complete payment" link that
   * (re-)opens Adyen's page - on a phone the payment tab is easily lost - and says where to come back.
   */
  protected readonly hostedWatching = signal(false);
  /** A payment that left the page is being finished after the shopper came back. */
  protected readonly resuming = signal(false);

  private hostedPopup: Window | null = null;
  /**
   * The payment window was opened by the "Complete payment" link, so this page holds no handle on it
   * and cannot see it close. The lock then holds until a result or the session's expiry - a missing
   * handle is not a closed window. Cancel payment could not close that window either, so the pane
   * asks the shopper to close it instead of offering Cancel.
   */
  protected readonly hostedWindowUntracked = signal(false);
  /**
   * Cancel payment was pressed on a hosted session and its last result check is still out. The
   * window may already be paid with the result not here yet (a lost postMessage, the next poll not
   * yet due), so nothing is torn down until that check answers.
   */
  protected readonly cancelling = signal(false);
  private hostedCancelCheck?: Subscription;
  private hostedMessageListener: ((event: MessageEvent) => void) | null = null;
  /**
   * Answers the return page's message with `{source: ACK_SOURCE, nonce}`, which is what lets that page
   * close itself. Lives until the checkout is torn down, not only while the result is awaited: a
   * result the poll took first still leaves the page waiting for this reply.
   */
  private hostedAckListener: ((event: MessageEvent) => void) | null = null;
  private hostedPollTimer: ReturnType<typeof setInterval> | null = null;
  private hostedPollRequest?: Subscription;
  /**
   * The popup has read as closed and the lock is released, but polling goes on - slowly - until the
   * session expires: `closed` can be a severed opener rather than a shut window.
   */
  private hostedWindowGone = false;
  private hostedNextPollAt = 0;
  private hostedWatchStartedAt = 0;
  /** Re-polls at once when the page comes back to the front: background tabs throttle timers, phones suspend them. */
  private hostedWakeListener: (() => void) | null = null;

  /** The return being finished, while it is. Its booking scope must still match before success is handed on. */
  private resume: AdyenResume | null = null;
  /** The session the stored resume record belongs to, so a failure clears only its own record. */
  private resumeRecordSessionId: string | null = null;
  /** The Drop-in session whose authorised result was handed on to the booking. */
  private handedOnSessionId: string | null = null;

  private dropin: { unmount?: () => void; remove?: () => void } | null = null;
  private mounting = false;
  /**
   * Set in ngOnDestroy. Only the session request is tied to the destroy ref; once
   * mountDropin() is past its first await nothing stops it, so switching the payment
   * method away mid-load would mount a Drop-in into a detached node on a dead instance
   * and - worse - park ITS session over the one a replacement instance just wrote, so a
   * later redirect return would resume the wrong payment.
   */
  private destroyed = false;
  /**
   * Bumped by every teardown. A session request or a mountDropin() that started under an
   * older value belongs to a checkout that has since been replaced - a changed amount, a
   * retry - and must neither mount nor touch the loading state of its successor.
   */
  private generation = 0;
  private sessionRequest?: Subscription;

  /** The amount the currently-mounted session was minted for. */
  private mountedAmount: number | null = null;

  /**
   * True from the moment the shopper's payment starts (beforeSubmit) until Adyen answers. A 3DS2
   * challenge keeps it open for tens of seconds, and the session in flight is the one that will be
   * charged: re-minting underneath it would leave its result pointing at a checkout that no longer
   * exists, and the booking would then be refused for the amount it was actually charged.
   */
  protected readonly paymentInFlight = signal(false);

  /**
   * Only a hosted payment can be cancelled. A Drop-in payment in flight has been submitted to Adyen
   * (beforeSubmit resolved) and may already be authorised: tearing it down would drop that result,
   * and the shopper would be charged with nothing booked. The SDK always answers a submitted
   * payment - completed, failed or an error; a 3DS2 challenge carries its own cancel and time-out -
   * so that lock always ends without this button.
   */
  protected canCancel(): boolean {
    return !!this.hostedSession() && !this.hostedPaid() && !this.cancelling();
  }
  /** The lock the previous run saw, so focus is put back only when a lock ENDS. */
  private wasLocked = false;

  /**
   * Errors after which paying again on a NEW session is safe. `pending` is deliberately
   * absent: that money may still arrive, and a second attempt could charge twice.
   */
  private static readonly RETRYABLE_ERRORS: readonly string[] = [
    'adyen.errors.session_failed',
    'adyen.errors.refused',
    'adyen.errors.cancelled',
    'adyen.errors.unexpected',
    // Adyen was unreachable or rate limited (the backend's 503): it clears on its own.
    ADYEN_ERROR.PANE_MESSAGES.TEMPORARILY_UNAVAILABLE,
    // The booking changed after a resumed payment: that payment is reversed on its own, so paying
    // for the booking as it is now is safe.
    ADYEN_RESUME.I18N.CONTEXT_CHANGED,
  ];
  readonly canRetry = computed(() => CwAdyenComponent.RETRYABLE_ERRORS.includes(this.errorMessage() ?? ''));

  /**
   * Re-mint whenever the total changes.
   *
   * The amount is baked into the Adyen session, so a mounted Drop-in keeps charging the
   * pre-change total - and the coupon field stays live beside it until a payment actually
   * starts. cakephp then refuses the booking because the paid amount does not match, and the
   * customer is charged and refunded for a booking they never got.
   *
   * Guarded on mountedAmount so it cannot fire before the first mount, and startCheckout()
   * writes no signal read here, so there is no loop.
   */
  constructor() {
    // Book stays disabled while a checkout loads: pressed then, it would reach no Drop-in and do
    // nothing - and every customer whose booking was refused lands on a Drop-in that is re-loading.
    effect(() => this.customEventService.adyenCheckoutLoadingChanged.emit(this.isLoading()));

    effect(() => {
      const price = this.price() ?? null;

      if (this.mountedAmount !== null && price !== this.mountedAmount && !untracked(this.paymentInFlight)) {
        this.teardown();
        this.startCheckout();
      }
    });

    // While locked, the control that had focus is disabled or removed (Book, Cancel payment) and
    // focus falls to the page. When the lock ends it is put back on the control that pays.
    effect(() => {
      const locked = this.paymentInFlight();
      if (this.wasLocked && !locked && !this.destroyed) {
        untracked(() => afterNextRender(() => this.restoreFocusAfterLock(), { injector: this.injector }));
      }
      this.wasLocked = locked;
    });
  }

  ngOnInit(): void {
    // Coming back from a payment that left the page takes priority: minting a new session here
    // would abandon a payment that is already in flight.
    const resume = this.adyenResumeService.takePendingResume();
    if (resume) {
      this.startResume(resume);
    } else {
      // Mount as soon as this component exists: the parent only renders it once
      // the customer has selected Adyen and there is something to pay, so its
      // presence in the DOM already IS the "start checkout" signal. Requiring a
      // Book click first would show the customer an empty box.
      this.startCheckout();
    }

    // Kept as an explicit re-trigger for the retry path (startCheckout() is a
    // no-op while a Drop-in is already mounted).
    this.customEventService.adyenPaymentFlowRequested
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        // A failed checkout is re-minted here only where the Retry button would also be
        // offered. After a Pending result the first payment may still settle, so a Book press
        // must not open a second payable session - the same rule RETRYABLE_ERRORS enforces on
        // Retry, which this re-trigger otherwise bypassed.
        if (this.errorMessage() && !this.canRetry()) {
          return;
        }
        // Deliberately NOT a teardown. The only emitter is "Book pressed", and a mounted
        // Drop-in at that moment holds the card details the customer has typed - re-minting
        // would wipe them. startCheckout() is a no-op while one is mounted, so this only
        // recovers a pane that has no Drop-in at all.
        this.startCheckout();
        // Drop-in has no pay button of its own, so Book pressing it IS the pay action.
        // Harmless when the fields are incomplete: Adyen validates them and marks its own
        // inputs without charging anything.
        this.submit();
      });

    // A booking refused after the payment was authorised has spent this session; the summary asks
    // for a new one so the customer can pay again without leaving the step. Same path as Retry.
    this.customEventService.adyenCheckoutResetRequested
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.retry());
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // The pane went away (another method, another step): a payment parked here and never sent to
    // /book is abandoned - teardown closes its window - so no later load may resume it. A record
    // already sent to /book stays: it is what says "already booked" next time.
    this.adyenResumeService.discardUnsubmitted(this.resumeRecordSessionId ?? undefined);
    this.teardown();
    // The effect above does not run for a destroyed component, so the release is explicit.
    this.customEventService.adyenCheckoutLoadingChanged.emit(false);
  }

  private startCheckout(): void {
    if (this.mounting || this.dropin || this.hostedSession()) {
      return;
    }

    const amount = this.price() ?? this.bookingService.selectedBookingData?.totalBookingAmount;
    if (!amount || amount <= 0) {
      this.fail('adyen.errors.session_failed');
      return;
    }

    this.mounting = true;
    const generation = this.generation;
    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.mountedAmount = this.price() ?? null;

    this.sessionRequest = this.paymentService.createAdyenSession({
      // The same builder feeds payment-method-view's availability check, so the session is minted
      // for exactly the booking Adyen was offered for.
      ...buildAdyenBookingContext(this.bookingService.selectedBookingData),
      amount,
      description: this.bookingService.selectedBookingData?.cartItems
        ?.map(item => item.service?.name)
        .filter(Boolean)
        .join(', '),
      // Both built from the CLEANED page URL, so a refused attempt's redirectResult is never carried
      // into the next session. The backend validates both against the request's Origin.
      return_url: this.adyenResumeService.cleanPageUrl(window.location.href),
      // Where a payment tab without an opener is sent back to. Never from the iframe embed: its frame
      // URL loaded as a top-level page would be a fresh widget, and it has no same-tab fallback to
      // resume - without return_to the return page tells the shopper to go back to their booking tab.
      ...(this.isIframeMode() ? {} : { return_to: this.adyenResumeService.cleanPageUrl(window.location.href) }),
      // `country.identifier` is the ISO code; `country_id` is a Calenso row id
      // and would mean nothing to Adyen. Country drives which methods Drop-in
      // offers, so getting it wrong silently hides TWINT.
      country_code: this.bookingService.partnerData?.country?.identifier,
      lang: this.translateService.currentLang,
      shopper_email: this.bookingService.selectedBookingData?.selectedUser?.eMail,
      metadata: {
        widget_type: 'smart_widget',
        partner_id: this.bookingService.partnerData?.id,
      },
      // The backend picks hosted on its own for an unregistered origin. The iframe embed always asks
      // for it: a redirect method (an issuer-forced 3DS redirect) cannot leave a cross-origin frame
      // and shows a blank page there.
      ...(this.isIframeMode() ? { checkout_mode: ADYEN_HOSTED.CHECKOUT_MODE_HOSTED } : {}),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: session => session?.mode === ADYEN_HOSTED.MODE.HOSTED
          ? this.mountHosted(session, generation)
          : void this.mountDropin(session, generation),
        error: (error: HttpErrorResponse) => {
          this.mounting = false;
          this.isLoading.set(false);
          // A 4xx is the backend's answer about THIS booking (e.g. its store has no Adyen
          // account) - asking again gets the same answer, so no retry is offered for it.
          // 429 is the exception: the throttle clears on its own, so it stays retryable.
          const isFinal = error?.status >= 400 && error?.status < 500 && error?.status !== 429;
          // Only the unexpected ones go to Sentry: a 4xx is configuration answering as designed.
          if (isFinal) {
            LoggerService.error(error);
          } else {
            // A real Error, named by status, so Sentry groups these instead of filing each
            // HttpErrorResponse object as its own "Non-Error exception captured".
            LoggerService.error(error);
            this.report(new Error(`adyen session ${error?.status ?? 'unknown'}`), 'session');
          }
          // The backend's own key says why, where it sent one; the status decides otherwise.
          this.fail(adyenSessionErrorMessageKey(error) ?? (isFinal ? ADYEN_ERROR.PANE_MESSAGES.UNAVAILABLE : 'adyen.errors.session_failed'));
        },
      });
  }

  private async mountDropin(
    session: CreateAdyenSessionResponse,
    generation: number = this.generation,
  ): Promise<void> {
    try {
      await this.adyenLoaderService.load(session.environment);
      const sdk = this.adyenLoaderService.getSdk();
      if (!sdk) {
        throw new Error('Adyen SDK global not present after load');
      }

      if (this.destroyed || generation !== this.generation) {
        return;
      }

      const checkout = await sdk.AdyenCheckout({
        environment: session.environment,
        clientKey: session.client_key,
        session: { id: session.session_id, sessionData: session.session_data },
        locale: this.toAdyenLocale(this.translateService.currentLang),
        analytics: { enabled: false },
        // Sessions-flow hook: fires when the shopper presses Pay, BEFORE the payment call. It is
        // the only "authorisation started" signal the SDK gives, and the summary needs it: a
        // 3DS2 challenge is tens of seconds during which the coupon form and the payment radios
        // would otherwise stay live, and the amount is baked into the session - a coupon applied
        // mid-challenge charges the old amount and the booking is then refused for the mismatch.
        // It MUST resolve(data), or the payment never starts. Never add onSubmit here: defining
        // it switches the SDK to the advanced flow and this integration has no /payments call.
        beforeSubmit: (
          data: unknown,
          _component: unknown,
          actions: { resolve: (data: unknown) => void; reject: () => void },
        ) => {
          if (this.destroyed || generation !== this.generation) {
            // A dead checkout must neither start a payment nor freeze its successor's summary.
            actions.reject();
            return;
          }
          this.paymentInFlight.set(true);
          this.customEventService.adyenPaymentStartedEvent.emit();
          // Parked before the payment call, so a page that dies once /book is sent still knows on
          // its next load that a booking may exist. Without the shopper's details: most payments
          // never leave the page, and beforeRedirect - the gate for the ones that do - stores the
          // full booking then.
          this.persistResume(ADYEN_RESUME.KIND.DROPIN, session, { includePersonalData: false });
          actions.resolve(data);
        },
        // Runs right before Drop-in navigates the page away (redirect 3DS). The page that comes back
        // has nothing but this record to finish the payment with, so a browser that will not keep
        // it stops the redirect - nothing is charged - instead of stranding the payment.
        beforeRedirect: (resolve: () => void, reject: () => void) => {
          if (this.destroyed || generation !== this.generation) {
            reject();
            return;
          }
          if (this.persistResume(ADYEN_RESUME.KIND.DROPIN, session)) {
            resolve();
            return;
          }
          reject();
          this.paymentInFlight.set(false);
          this.fail('adyen.errors.session_failed');
        },
        onPaymentCompleted: (result: { resultCode?: string; sessionResult?: string }) =>
          this.onCompleted(session, result, generation),
        onPaymentFailed: (result: { resultCode?: string }) => this.onFailed(result, generation),
        onError: (error: unknown) => {
          // Still reported - but a checkout the customer has already left must not act on it.
          // Of the SDK's callbacks this is the one that needs no Pay press, so it is the one
          // most likely to arrive late: it would wipe the session a REPLACEMENT instance has
          // parked and paint an error over its live Drop-in. A checkout replaced inside this
          // same instance (a coupon re-mint bumps the generation) is just as dead.
          LoggerService.error(error);
          if (this.destroyed || generation !== this.generation) {
            return;
          }
          this.fail('adyen.errors.unexpected');
        },
      });

      if (this.destroyed || generation !== this.generation) {
        return;
      }

      const container = this.dropinContainer()?.nativeElement;
      if (!container) {
        // Failing loudly beats a permanent empty pane.
        this.fail('adyen.errors.unexpected');
        return;
      }
      // In the light DOM Drop-in would inherit the host page's font (the slot host is reset to
      // `initial` from the shadow root), so give its container the widget's own font.
      container.style.fontFamily = getComputedStyle(this.host.nativeElement).fontFamily;

      // Adyen answers the session's /setup with the methods this merchant account can
      // actually offer for THIS amount, currency and country. When that list comes back
      // empty the Drop-in still mounts - as an empty, zero-height container - so the
      // customer sees the card option selected, then nothing at all, and a submit that
      // cannot work. Nothing throws and no callback fires, which is why this has to be
      // checked rather than waited for. Observed on DEV: a CH merchant account asked for
      // USD returned `paymentMethods: {}` and the box rendered 0px high.
      if (this.offeredPaymentMethodCount(checkout) === 0) {
        this.report(new Error('Adyen session offers no payment methods'), 'no_payment_methods');
        this.fail(ADYEN_ERROR.PANE_MESSAGES.UNAVAILABLE);
        return;
      }

      this.dropin = new sdk.Dropin(checkout, {
        showStoredPaymentMethods: false,
        // No second pay button. Drop-in ships its own, which would let the shopper be charged
        // before the booking form is valid, and leaves "pay" and "book" as two separate presses.
        // The widget's own submit drives this Drop-in through submit() instead, after validation.
        showPayButton: false,
      }).mount(container);
    } catch (error) {
      this.report(error, 'mount');
      if (generation !== this.generation) {
        return;
      }
      this.fail('adyen.errors.unexpected');
    } finally {
      // A replaced checkout must not clear the flags its successor just set.
      if (generation === this.generation) {
        this.mounting = false;
        this.isLoading.set(false);
      }
    }
  }

  /**
   * Shows the hosted pay button in place of Drop-in. No SDK is loaded. Nothing is parked for a resume
   * yet either: openHostedPayment() parks it when the shopper actually pays.
   */
  private mountHosted(session: CreateAdyenSessionResponse, generation: number): void {
    if (this.destroyed || generation !== this.generation) {
      return;
    }
    this.mounting = false;
    this.isLoading.set(false);

    // Without the token the result can never be read back: a session the shopper could pay but
    // this page could never book.
    if (!session.url || !session.hosted_nonce || !session.hosted_token) {
      this.report(new Error('Adyen hosted session without url, nonce or token'), 'hosted_session');
      this.fail('adyen.errors.unexpected');
      return;
    }

    this.hostedPaid.set(false);
    this.hostedPopupBlocked.set(false);
    this.hostedSession.set(session);
  }

  /**
   * Opens Adyen's hosted page. Must run synchronously inside the shopper's click - the pay button's
   * or Book's (through submit()) - or the browser blocks the popup. The session was minted when the
   * pane mounted, so nothing is awaited before window.open.
   *
   * A blocked popup falls back per embed: the web component takes the page itself to Adyen (the
   * record parked first, the return page sends the shopper back with `?adyen_hosted=<nonce>`); the
   * iframe embed cannot navigate its cross-origin frame, so it offers the "Complete payment" link.
   *
   * True when the shopper is on their way to pay afterwards.
   */
  openHostedPayment(): boolean {
    const session = this.hostedSession();
    if (!session?.url || !session.hosted_nonce || this.hostedPaid()) {
      return false;
    }

    if (this.hostedPopup && !this.hostedPopup.closed) {
      // Already paying: bring the window back rather than reloading Adyen's page in it.
      try {
        this.hostedPopup.focus();
      } catch (error) {
        LoggerService.error(error);
      }
      return true;
    }

    // Web component: parked BEFORE the window opens (synchronous storage, still inside the click). The
    // return page sends a payment tab that lost its opener back to this page with the nonce, and
    // that tab can only finish the booking from this record. Two tabs may then both book; the backend
    // claims the session once and answers the other "already booked".
    if (!this.isIframeMode()) {
      this.persistResume(ADYEN_RESUME.KIND.HOSTED, session);
    }

    let popup: Window | null = null;
    try {
      popup = window.open(session.url, ADYEN_HOSTED.POPUP_NAME, ADYEN_HOSTED.POPUP_FEATURES);
    } catch (error) {
      LoggerService.error(error);
    }

    if (!popup) {
      if (!this.isIframeMode() && this.continueHostedInThisTab(session)) {
        return true;
      }
      this.hostedPopupBlocked.set(true);
      return false;
    }

    this.hostedPopupBlocked.set(false);
    this.hostedPopup = popup;
    this.hostedWindowUntracked.set(false);
    this.startHostedPayment(session);

    return true;
  }

  /**
   * The "Complete payment" link was pressed. The anchor itself opens Adyen's page (a real link click
   * is let through where a scripted window.open was blocked, and it targets the same named window);
   * this only starts waiting for the result, if nothing is waiting yet.
   */
  onCompletePaymentLinkClick(): void {
    const session = this.hostedSession();
    if (!session || this.hostedPaid()) {
      return;
    }
    this.hostedPopupBlocked.set(false);
    if (!this.hostedWatching()) {
      this.startHostedPayment(session);
      this.hostedWindowUntracked.set(true);
      return;
    }
    // Already waiting. A popup this page still holds is simply re-navigated by the link. Without one
    // - it read as closed and the lock was released - the link opens a window this page cannot see,
    // and the shopper may pay in it: the lock comes back and stays until a result or expiry.
    if (!this.hostedPopup || this.hostedPopup.closed) {
      this.hostedPopup = null;
      this.hostedWindowUntracked.set(true);
      this.hostedWindowGone = false;
      if (!this.paymentInFlight()) {
        this.paymentInFlight.set(true);
        this.customEventService.adyenPaymentStartedEvent.emit();
      }
    }
  }

  /** Same lock as Drop-in's beforeSubmit: the amount is baked into this session, so the summary stays frozen while the shopper may be paying. */
  private startHostedPayment(session: CreateAdyenSessionResponse): void {
    this.paymentInFlight.set(true);
    this.customEventService.adyenPaymentStartedEvent.emit();
    this.watchHostedResult(session, this.generation);
  }

  /**
   * The popup was blocked on a web-component embed: the page itself goes to Adyen. Only once the
   * resume record is stored - a page that comes back without it could never book what was paid.
   */
  private continueHostedInThisTab(session: CreateAdyenSessionResponse): boolean {
    if (!this.persistResume(ADYEN_RESUME.KIND.HOSTED, session, { leftPage: true })) {
      return false;
    }
    this.paymentInFlight.set(true);
    this.customEventService.adyenPaymentStartedEvent.emit();
    try {
      this.navigateTo(session.url as string);

      return true;
    } catch (error) {
      LoggerService.error(error);
      this.paymentInFlight.set(false);
      this.clearResumeRecord();

      return false;
    }
  }

  /**
   * Waits for the hosted result on two channels, whichever answers first: the return page's
   * postMessage (fast, but only reaches a page that is still the popup's opener) and polling the
   * API (works when the message never arrives). The first valid answer wins; settleHosted()
   * ignores the rest.
   */
  private watchHostedResult(session: CreateAdyenSessionResponse, generation: number): void {
    this.stopHostedWatch();
    this.hostedWindowGone = false;
    this.hostedNextPollAt = 0;
    this.hostedWatchStartedAt = Date.now();
    this.hostedWatching.set(true);

    const apiOrigin = adyenApiOrigin(environment.restAPI);
    const nonce = session.hosted_nonce as string;
    // Registered before the settling listener: a settle that fails (context changed) tears the
    // checkout down inside the same dispatch, and the return page must still be told to close.
    this.stopHostedAck();
    this.hostedAckListener = (event: MessageEvent) => {
      if (adyenHostedMessageResult(event, apiOrigin, nonce, session.session_id)) {
        this.acknowledgeHostedMessage(event, apiOrigin as string, nonce);
      }
    };
    window.addEventListener('message', this.hostedAckListener);
    this.hostedMessageListener = (event: MessageEvent) => {
      const sessionResult = adyenHostedMessageResult(event, apiOrigin, nonce, session.session_id);
      if (sessionResult) {
        this.settleHosted(session, sessionResult, generation);
      }
    };
    window.addEventListener('message', this.hostedMessageListener);

    const expiresAt = Date.parse(session.expires_at ?? '');
    const deadline = Number.isFinite(expiresAt) ? expiresAt : Date.now() + ADYEN_HOSTED.EXPIRY_FALLBACK_MS;
    this.hostedPollTimer = setInterval(
      () => this.pollHostedResult(session, generation, deadline),
      ADYEN_HOSTED.POLL_INTERVAL_MS,
    );

    // Polling is the source of truth, and a phone that suspended this tab while the shopper paid in
    // another one fires its timers late: ask again the moment the page is back in front.
    this.hostedWakeListener = () => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        this.pollHostedResult(session, generation, deadline, true);
      }
    };
    document.addEventListener('visibilitychange', this.hostedWakeListener);
    window.addEventListener('focus', this.hostedWakeListener);
  }

  /**
   * One poll. Every POLL_INTERVAL_MS at first, then - once the window reads as closed, or after
   * FAST_POLL_WINDOW_MS - every CLOSED_POLL_INTERVAL_MS. `force` (the page came back to the front)
   * skips that pacing, never the in-flight guard.
   *
   * A popup that reads as closed releases the lock once its poll comes back empty, but does NOT end
   * the watch: a Cross-Origin-Opener-Policy on Adyen's or the issuer's page makes an open window read
   * as closed, and the shopper may still be paying in it. A result that lands meanwhile still
   * settles, once. Only the expiry poll ends the watch.
   */
  private pollHostedResult(
    session: CreateAdyenSessionResponse,
    generation: number,
    deadline: number,
    force = false,
  ): void {
    // `closed`, not presence: an answer that arrives synchronously finishes before the subscription
    // is even assigned, and a presence check would then read it as in flight forever.
    if ((this.hostedPollRequest && !this.hostedPollRequest.closed) || this.destroyed || generation !== this.generation) {
      return;
    }

    const now = Date.now();
    const expired = now >= deadline;
    const slow = this.hostedWindowGone || now - this.hostedWatchStartedAt >= ADYEN_HOSTED.FAST_POLL_WINDOW_MS;
    if (slow && !force && !expired && now < this.hostedNextPollAt) {
      return;
    }
    this.hostedNextPollAt = now + ADYEN_HOSTED.CLOSED_POLL_INTERVAL_MS;

    const windowClosed = !this.hostedWindowUntracked() && (!this.hostedPopup || this.hostedPopup.closed);
    const finish = (): void => {
      if (generation !== this.generation || this.hostedPaid()) {
        return;
      }
      if (expired) {
        this.expireHosted();
      } else if (windowClosed && !this.hostedWindowGone) {
        this.releaseHostedLock();
      }
    };

    this.hostedPollRequest = this.paymentService.getAdyenHostedResult(
      session.hosted_nonce as string,
      session.hosted_token as string,
    ).subscribe({
      next: (result: AdyenHostedResultResponse) => {
        this.hostedPollRequest = undefined;
        const sessionResult = this.returnedSessionResult(session, result);
        if (sessionResult) {
          this.settleHosted(session, sessionResult, generation);
          return;
        }
        finish();
      },
      error: (error: unknown) => {
        this.hostedPollRequest = undefined;
        LoggerService.error(error);
        finish();
      },
    });
  }

  /** The session result a hosted-result answer carries for THIS session, or null while it is not paid. */
  private returnedSessionResult(
    session: CreateAdyenSessionResponse,
    result: AdyenHostedResultResponse | null | undefined,
  ): string | null {
    return result?.status === ADYEN_HOSTED.RESULT_STATUS.RETURNED
      && result.session_id === session.session_id
      && typeof result.session_result === 'string'
      && result.session_result !== ''
      ? result.session_result
      : null;
  }

  /**
   * Hands the hosted result on - the same event, with the same payload, as a completed Drop-in, so
   * /book is untouched. The backend reads the outcome from Adyen itself; a result for a declined
   * payment is refused there exactly as one from Drop-in would be.
   */
  private settleHosted(session: CreateAdyenSessionResponse, sessionResult: string, generation: number): void {
    if (this.hostedPaid() || this.destroyed || generation !== this.generation) {
      return;
    }
    this.hostedPaid.set(true);
    this.stopHostedWatch();
    this.hostedPopup = null;
    this.paymentInFlight.set(false);

    if (!this.resumeScopeMatches()) {
      this.failContextChanged();
      return;
    }
    this.finishResume();

    this.customEventService.adyenSuccessfulEvent.emit({
      session_id: session.session_id,
      session_result: sessionResult,
    });
  }

  /**
   * The window reads as closed without a result. Closing it is not an error - the shopper may
   * simply want another method - so the lock is released silently and the button stays. The watch
   * keeps polling (see pollHostedResult), because the window may only look closed.
   */
  private releaseHostedLock(): void {
    this.hostedWindowGone = true;
    this.hostedPopup = null;
    this.paymentInFlight.set(false);
    this.customEventService.resetAdyenEvent.emit(undefined);
  }

  /**
   * The session ran out without a result. It can no longer be paid, so it fails, retryably, onto a
   * new one.
   */
  private expireHosted(): void {
    this.stopHostedWatch();
    this.paymentInFlight.set(false);
    this.fail('adyen.errors.session_failed');
  }

  private stopHostedWatch(): void {
    this.hostedWatching.set(false);
    if (this.hostedMessageListener) {
      window.removeEventListener('message', this.hostedMessageListener);
      this.hostedMessageListener = null;
    }
    if (this.hostedWakeListener) {
      document.removeEventListener('visibilitychange', this.hostedWakeListener);
      window.removeEventListener('focus', this.hostedWakeListener);
      this.hostedWakeListener = null;
    }
    if (this.hostedPollTimer !== null) {
      clearInterval(this.hostedPollTimer);
      this.hostedPollTimer = null;
    }
    this.hostedPollRequest?.unsubscribe();
    this.hostedPollRequest = undefined;
  }

  private stopHostedAck(): void {
    if (this.hostedAckListener) {
      window.removeEventListener('message', this.hostedAckListener);
      this.hostedAckListener = null;
    }
  }

  /** Tells the return page its message was taken, to the API origin only - the page then closes itself. */
  private acknowledgeHostedMessage(event: MessageEvent, apiOrigin: string, nonce: string): void {
    try {
      (event.source as Window | null)?.postMessage({ source: ADYEN_HOSTED.ACK_SOURCE, nonce }, apiOrigin);
    } catch (error) {
      LoggerService.error(error);
    }
  }

  /**
   * Only `Authorised` may proceed — `Pending`/`Received` describe money that may
   * still never arrive, and booking against those creates a confirmed seat for
   * a payment that can still fail.
   *
   * The resume record is deliberately kept on success: `/book` marks it as sent and drops it once
   * answered, so a page that dies in between knows on its next load that a booking may exist.
   */
  private onCompleted(
    session: CreateAdyenSessionResponse,
    result: { resultCode?: string; sessionResult?: string },
    generation: number,
  ): void {
    // A replaced checkout's result belongs to no live Drop-in, which is the same hazard as a
    // destroyed one: acting on it would book under whatever this component shows NOW.
    if (this.destroyed || generation !== this.generation) {
      // The customer left this payment method while the SDK was still working. Acting on the
      // result now would book under whatever method is selected NOW. Leaving it alone is the
      // safe outcome: an authorised payment with no booking is reversed by the reconciliation job.
      LoggerService.error(new Error('Adyen reported a result to a destroyed checkout - ignored'));
      return;
    }
    this.paymentInFlight.set(false);

    if (result?.resultCode !== ADYEN_RESULT_CODE.AUTHORISED || !result?.sessionResult) {
      this.fail(
        result?.resultCode === ADYEN_RESULT_CODE.PENDING || result?.resultCode === ADYEN_RESULT_CODE.RECEIVED
          ? 'adyen.errors.pending'
          : 'adyen.errors.refused'
      );
      return;
    }

    // One booking per payment: a repeated callback for a session already handed on is dropped.
    if (this.handedOnSessionId === session.session_id) {
      return;
    }
    if (!this.resumeScopeMatches()) {
      this.failContextChanged();
      return;
    }
    this.finishResume();
    this.handedOnSessionId = session.session_id;

    this.customEventService.adyenSuccessfulEvent.emit({
      session_id: session.session_id,
      session_result: result.sessionResult,
    });
  }

  private onFailed(result: { resultCode?: string }, generation: number): void {
    if (this.destroyed || generation !== this.generation) {
      return;
    }
    this.paymentInFlight.set(false);
    this.fail(
      result?.resultCode === ADYEN_RESULT_CODE.CANCELLED
        ? 'adyen.errors.cancelled'
        : 'adyen.errors.refused'
    );
  }

  /**
   * Finishes a payment that left the page and came back. The booking check runs FIRST: a record
   * already sent to `/book` is "already booked", never paid again; a booking that changed since the
   * payment started is refused before anything is submitted, and that payment is left to the
   * reversal job.
   */
  private startResume(resume: AdyenResume): void {
    this.resumeRecordSessionId = resume.record.sessionId;
    // Not armed for re-minting: a resume is money already in flight, whatever the price input does.
    this.mountedAmount = null;

    if (resume.bookingSubmitted) {
      // Never paid again: the summary replays the /book that was sent, and the backend's answer
      // decides. Normally the summary takes such a resume before this pane mounts; this is the
      // backstop, handing it back.
      this.resumeRecordSessionId = null;
      this.adyenResumeService.setPendingResume(resume);
      this.customEventService.adyenSubmittedResumeEvent.emit();
      return;
    }

    this.resume = resume;
    if (!this.resumeScopeMatches()) {
      this.failContextChanged();
      return;
    }

    this.resuming.set(true);
    // The summary stays frozen while the payment is finished - same lock as a payment in progress.
    this.paymentInFlight.set(true);
    this.customEventService.adyenPaymentStartedEvent.emit();

    if (resume.record.kind === ADYEN_RESUME.KIND.HOSTED) {
      this.resumeHosted(resume);
    } else {
      void this.resumeDropin(resume, this.generation);
    }
  }

  /**
   * Redirect 3DS came back: a fresh AdyenCheckout for the SAME session, then the redirect result is
   * submitted - Adyen's documented Sessions-flow return, which then calls onPaymentCompleted /
   * onPaymentFailed like any payment. No Drop-in is mounted: there is nothing left to type.
   */
  private async resumeDropin(resume: AdyenResume, generation: number): Promise<void> {
    const record = resume.record;
    const session = this.sessionFromRecord(resume);
    this.mounting = true;
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      await this.adyenLoaderService.load(record.environment);
      const sdk = this.adyenLoaderService.getSdk();
      if (!sdk) {
        throw new Error('Adyen SDK global not present after load');
      }
      if (this.destroyed || generation !== this.generation) {
        return;
      }

      const checkout = await sdk.AdyenCheckout({
        environment: record.environment,
        clientKey: record.clientKey,
        session: { id: record.sessionId },
        locale: this.toAdyenLocale(this.translateService.currentLang),
        analytics: { enabled: false },
        onPaymentCompleted: (result: { resultCode?: string; sessionResult?: string }) =>
          this.onCompleted(session, result, generation),
        onPaymentFailed: (result: { resultCode?: string }) => this.onFailed(result, generation),
        onError: (error: unknown) => {
          LoggerService.error(error);
          if (this.destroyed || generation !== this.generation) {
            return;
          }
          this.fail('adyen.errors.unexpected');
        },
      }) as { submitDetails?: (data: { details: { redirectResult: string } }) => void } | null;

      if (this.destroyed || generation !== this.generation) {
        return;
      }
      if (typeof checkout?.submitDetails !== 'function' || !resume.redirectResult) {
        throw new Error('Adyen checkout cannot submit the redirect result');
      }
      checkout.submitDetails({ details: { redirectResult: resume.redirectResult } });
    } catch (error) {
      this.report(error, 'resume');
      if (generation !== this.generation) {
        return;
      }
      this.fail('adyen.errors.unexpected');
    } finally {
      if (generation === this.generation) {
        this.mounting = false;
        this.isLoading.set(false);
      }
    }
  }

  /**
   * The hosted page came back to this tab. The API's return page stored the result before sending
   * the shopper here, so the first poll - with the nonce AND the token - normally answers at once.
   */
  private resumeHosted(resume: AdyenResume): void {
    const session = this.sessionFromRecord(resume);
    if (!session.url || !session.hosted_nonce || !session.hosted_token) {
      this.report(new Error('Adyen hosted resume without url, nonce or token'), 'resume');
      this.fail('adyen.errors.unexpected');
      return;
    }
    this.hostedPaid.set(false);
    this.hostedPopupBlocked.set(false);
    this.hostedSession.set(session);
    this.watchHostedResult(session, this.generation);
    const expiresAt = Date.parse(session.expires_at ?? '');
    this.pollHostedResult(
      session,
      this.generation,
      Number.isFinite(expiresAt) ? expiresAt : Date.now() + ADYEN_HOSTED.EXPIRY_FALLBACK_MS,
      true,
    );
  }

  private sessionFromRecord(resume: AdyenResume): CreateAdyenSessionResponse {
    const record = resume.record;

    return {
      session_id: record.sessionId,
      session_data: record.sessionData ?? null,
      client_key: record.clientKey ?? null,
      environment: record.environment,
      merchant_reference: '',
      // The record's amount, never the current price: the session was minted for exactly this.
      amount: record.amount,
      currency: record.currency ?? '',
      expires_at: record.expiresAt ?? null,
      mode: record.kind === ADYEN_RESUME.KIND.HOSTED ? ADYEN_HOSTED.MODE.HOSTED : ADYEN_HOSTED.MODE.EMBEDDED,
      url: record.url ?? null,
      hosted_nonce: record.nonce ?? null,
      hosted_token: record.token ?? null,
    };
  }

  /** True outside a resume, or while the booking on the page is still the one the resumed payment was made for. */
  private resumeScopeMatches(): boolean {
    return !this.resume
      || buildAdyenHoldScope(this.bookingService.selectedBookingData) === this.resume.record.holdScope;
  }

  /** The payment belongs to another booking than the one on the page now; the reversal job refunds it. */
  private failContextChanged(): void {
    this.paymentInFlight.set(false);
    this.fail(ADYEN_RESUME.I18N.CONTEXT_CHANGED);
  }

  private finishResume(): void {
    this.resume = null;
    this.resuming.set(false);
  }

  /**
   * Parks this payment so a page that navigates away can finish it on the way back. False when the
   * browser would not keep it.
   */
  private persistResume(
    kind: AdyenResumeKind,
    session: CreateAdyenSessionResponse,
    options: { includePersonalData?: boolean; leftPage?: boolean } = {},
  ): boolean {
    const bookingData = this.bookingService.selectedBookingData;
    const saved = this.adyenResumeService.save({
      v: ADYEN_RESUME.VERSION,
      kind,
      partnerUuid: this.widgetService?.partnerUUID,
      widgetUuid: this.widgetService?.widgetUUID,
      sessionId: session.session_id,
      sessionData: session.session_data,
      clientKey: session.client_key,
      environment: session.environment,
      nonce: session.hosted_nonce ?? null,
      token: session.hosted_token ?? null,
      url: session.url ?? null,
      leftPage: !!options.leftPage,
      expiresAt: session.expires_at ?? null,
      amount: this.price() ?? bookingData?.totalBookingAmount,
      currency: session.currency,
      holdScope: buildAdyenHoldScope(bookingData),
      booking: this.adyenResumeService.snapshot(bookingData, options.includePersonalData ?? true),
      submittedAt: null,
      createdAt: Date.now(),
    });
    if (saved) {
      this.resumeRecordSessionId = session.session_id;
    }

    return saved;
  }

  /** Drops the resume record this checkout wrote or resumed - never one a newer attempt has since written. */
  private clearResumeRecord(): void {
    if (this.resumeRecordSessionId) {
      this.adyenResumeService.clearIfOwnedBy(this.resumeRecordSessionId);
      this.resumeRecordSessionId = null;
    }
  }

  /** The iframe embed: a cross-origin frame that must never navigate itself to a payment page. */
  private isIframeMode(): boolean {
    return !this.widgetService?.isWebComponentMode;
  }

  protected get isIframeEmbed(): boolean {
    return this.isIframeMode();
  }

  /** The one place this component navigates the page. */
  private navigateTo(url: string): void {
    window.location.assign(url);
  }

  private teardown(): void {
    try {
      this.dropin?.unmount?.();
      this.dropin?.remove?.();
    } catch (error) {
      LoggerService.error(error);
    }
    this.dropin = null;
    // A payment still in flight belonged to the checkout being dropped; its result is ignored now.
    this.paymentInFlight.set(false);

    // A hosted window left open would keep taking a payment for a session nothing listens to.
    this.stopHostedWatch();
    try {
      if (this.hostedPopup && !this.hostedPopup.closed) {
        this.hostedPopup.close();
      }
    } catch (error) {
      LoggerService.error(error);
    }
    this.hostedPopup = null;
    this.hostedWindowUntracked.set(false);
    this.hostedCancelCheck?.unsubscribe();
    this.hostedCancelCheck = undefined;
    this.cancelling.set(false);
    this.stopHostedAck();
    this.hostedSession.set(null);
    this.hostedPopupBlocked.set(false);
    this.hostedPaid.set(false);

    // Cancel whatever is still in flight for the checkout being replaced. Without this,
    // startCheckout() bailed on `mounting` and a price change during the session request
    // was dropped: the Drop-in then mounted on the OLD amount.
    this.generation++;
    this.sessionRequest?.unsubscribe();
    this.sessionRequest = undefined;
    this.mounting = false;
    this.isLoading.set(false);
  }

  /** A declined or cancelled payment is final for its session, so retrying mints a new one. */
  retry(): void {
    this.teardown();
    this.errorMessage.set(null);
    this.startCheckout();
  }

  /**
   * The shopper gives up on the hosted payment in progress - the one way out while it locks the
   * summary.
   *
   * The window may already be paid while its result has not reached this page (the return page's
   * message lost, the next poll not yet due), so the result is asked for once more first. Paid: it is
   * settled exactly as a poll would have settled it, and books once. Not paid: the payment window
   * this page holds is closed, the parked payment forgotten and a fresh checkout started, so the
   * summary unlocks with nothing paid. A payment that still completes on the abandoned session books
   * nothing and is reversed by the reconciliation job, exactly as one abandoned by switching the
   * method away.
   *
   * Not while a returned payment is being finished (that money may already be taken), and never for
   * Drop-in (see canCancel).
   */
  cancelPayment(): void {
    const session = this.hostedSession();
    if (
      !this.paymentInFlight() || this.resuming() || this.cancelling()
      || !session?.hosted_nonce || !session.hosted_token || this.hostedPaid()
    ) {
      return;
    }
    this.cancelling.set(true);
    const generation = this.generation;
    const check = this.paymentService.getAdyenHostedResult(session.hosted_nonce, session.hosted_token).subscribe({
      next: (result: AdyenHostedResultResponse) =>
        this.finishCancel(session, generation, this.returnedSessionResult(session, result)),
      // No answer counts as unpaid, as before this check existed: the shopper asked to stop, and a
      // payment that did complete is reversed by the reconciliation job.
      error: (error: unknown) => {
        LoggerService.error(error);
        this.finishCancel(session, generation, null);
      },
    });
    // An answer that arrived synchronously has already finished; only one still out is kept.
    this.hostedCancelCheck = check.closed ? undefined : check;
  }

  private finishCancel(session: CreateAdyenSessionResponse, generation: number, sessionResult: string | null): void {
    this.hostedCancelCheck = undefined;
    this.cancelling.set(false);
    // Settled by the message or a poll, expired, or released by the window closing while the check
    // was out: that outcome stands.
    if (this.destroyed || generation !== this.generation || this.hostedPaid() || !this.paymentInFlight()) {
      return;
    }
    if (sessionResult) {
      this.settleHosted(session, sessionResult, generation);
      return;
    }
    this.clearResumeRecord();
    this.teardown();
    this.errorMessage.set(null);
    this.customEventService.resetAdyenEvent.emit(undefined);
    this.startCheckout();
  }

  /**
   * Puts keyboard focus back on the control that pays once a payment lock ends: the hosted pay
   * button, else Book (Drop-in's pay action, rendered by the payment step around this pane), else
   * the pane itself while a fresh checkout loads. Only when the lock took focus away - a shopper who
   * moved on to another field keeps it.
   */
  private restoreFocusAfterLock(): void {
    if (this.destroyed || this.paymentInFlight()) {
      return;
    }
    const host = this.host.nativeElement;
    const root = getDomRoot(host);
    const pageFocus = host.ownerDocument.activeElement;
    const focusLost = !pageFocus || pageFocus === host.ownerDocument.body;
    const focusInPane = !!root.activeElement && host.contains(root.activeElement);
    if (!focusLost && !focusInPane) {
      return;
    }
    const button = [
      // The <button> itself: an <app-button> host given a static id carries it too.
      host.querySelector<HTMLButtonElement>('button#cw-adyen-hosted-pay'),
      root.querySelector<HTMLButtonElement>('button#payment-method-book-btn'),
    ].find(candidate => !!candidate && !candidate.disabled);
    (button ?? host.querySelector<HTMLElement>('.cw-adyen'))?.focus();
  }

  /**
   * Pay now, driven by the widget's own submit button (Drop-in has no button of its own).
   *
   * False when there is nothing to submit - no mounted Drop-in, or the card fields are
   * incomplete - so the caller leaves the shopper on the form instead of reporting a payment
   * failure that never happened. The outcome arrives asynchronously via adyenSuccessfulEvent.
   */
  submit(): boolean {
    if (this.hostedSession()) {
      // Book is still the pay action on a hosted session: it opens the payment window, and this
      // runs synchronously inside that click, so the browser allows the popup.
      return this.openHostedPayment();
    }

    const dropin = this.dropin as { submit?: () => void } | null;
    if (!dropin || typeof dropin.submit !== 'function') {
      return false;
    }

    try {
      dropin.submit();

      return true;
    } catch (error) {
      LoggerService.error(error);

      return false;
    }
  }

  private fail(translationKey: string): void {
    // Disarm the price effect: after a failure the pane shows the outcome and, where allowed, a
    // Retry button - which mints for the CURRENT price anyway. Left armed, a coupon applied after
    // a Pending result would mint a second payable session while the first may still settle,
    // which is the double charge RETRYABLE_ERRORS exists to prevent.
    this.mountedAmount = null;
    // Refused, expired, context changed, already booked: the parked payment is final either way, and a
    // Retry mints a fresh session, so nothing may resume this one again.
    this.clearResumeRecord();
    this.finishResume();
    this.teardown();
    this.errorMessage.set(translationKey);
    this.customEventService.resetAdyenEvent.emit(translationKey);
  }

  /**
   * How many payment methods the minted session actually offers.
   *
   * Returns `null` when the SDK does not expose the shape we expect. That distinction is
   * the point: the caller refuses ONLY on a positively-observed zero, so an SDK upgrade
   * that moves this property can never block a customer who could otherwise have paid.
   * Reading it wrong must degrade to today's behaviour, not to "card payment is down".
   */
  private offeredPaymentMethodCount(checkout: unknown): number | null {
    const response = (checkout as { paymentMethodsResponse?: unknown } | null)?.paymentMethodsResponse;
    if (!response || typeof response !== 'object') {
      return null;
    }

    const methods = (response as { paymentMethods?: unknown }).paymentMethods;

    if (Array.isArray(methods)) {
      return methods.length;
    }

    // The shape this guard was written for is an empty OBJECT, not an empty array: the observed
    // DEV answer was `paymentMethods: {}`. An Array.isArray-only test returns null for it - "I
    // could not tell" - and the caller only refuses on 0, so the guard never fired for the one
    // case it exists to catch. Count the keys instead, and keep null for a genuinely unreadable
    // response so an unknown shape still mounts rather than blocking a working checkout.
    if (methods && typeof methods === 'object') {
      return Object.keys(methods).length;
    }

    return null;
  }

  /**
   * Logged AND sent to Sentry: a payment form that never renders produces no failed request and
   * nothing the partner would ever see - the tags are what make it findable.
   */
  private report(error: unknown, stage: string): void {
    LoggerService.error(error);
    captureException(
      error instanceof Error ? error : new Error(`adyen ${stage}: ${String(error)}`),
      { tags: { psp: 'adyen', stage } },
    );
  }

  private toAdyenLocale(lang?: string): string {
    switch ((lang ?? '').toLowerCase().slice(0, 2)) {
      case 'de': return 'de-CH';
      case 'fr': return 'fr-CH';
      case 'it': return 'it-CH';
      case 'nl': return 'nl-NL';
      case 'fi': return 'fi-FI';
      case 'pl': return 'pl-PL';
      case 'es': return 'es-ES';
      case 'sv': return 'sv-SE';
      default: return 'en-US';
    }
  }
}
